import type { BookableService } from "../api/journal-actions.api"

import type { BookingState } from "./booking-draft"

/**
 * 5-А2 «Запись с телефона по шагам» — порядок и готовность шагов, отдельно от разметки.
 *
 * ПОЧЕМУ НА ТЕЛЕФОНЕ ФОРМА ДЕЛИТСЯ НА ШАГИ. Одна и та же форма на ноутбуке — это
 * шесть полей в поле зрения: администратор видит весь заказ разом и правит любое.
 * На 360 px те же шесть полей становятся лентой в два экрана, и человек с трубкой
 * в руке теряет в ней место: он листает вверх проверить услугу, теряет выбранное
 * время, листает вниз — и кладёт трубку, чтобы «записать потом». Шаги делают
 * видимым ровно то, о чём сейчас разговор, и называют, сколько осталось.
 *
 * ПОРЯДОК ИЗ ТЗ И МАКЕТА: клиент → услуги → мастер → время → подтверждение. Он не
 * произволен: время нельзя предложить, пока неизвестны услуги (длительность) и мастер
 * (чьё расписание смотреть), а подтверждать нечего, пока не выбрано время.
 *
 * ШАГ НЕ ЗАПИРАЕТ НАЗАД. «Назад» доступен всегда и ничего не стирает: черновик один,
 * шаги — это только то, какую его часть сейчас видно.
 */

export const BOOKING_STEP_NAMES = ["клиент", "услуги", "мастер", "время", "подтверждение"] as const
export type BookingStep = 1 | 2 | 3 | 4 | 5
export const BOOKING_STEP_COUNT = 5

export function stepName(step: BookingStep): string {
  return BOOKING_STEP_NAMES[step - 1] ?? ""
}

/** «Шаг 2 из 5 · услуги» — дословно из макета. */
export function stepLabel(step: BookingStep): string {
  return `Шаг ${String(step)} из ${String(BOOKING_STEP_COUNT)} · ${stepName(step)}`
}

/**
 * Что мешает уйти с этого шага дальше. null — можно.
 *
 * ПРИЧИНА ВОЗВРАЩАЕТСЯ СЛОВАМИ, А НЕ ЛОЖЬЮ. Выключенная кнопка «Дальше» без
 * объяснения — это тупик: человек жмёт её три раза и звонит администратору. Причина
 * показывается подсказкой на самой кнопке, до нажатия.
 */
export function stepBlocker(state: BookingState, step: BookingStep): string | null {
  const { draft } = state
  switch (step) {
    case 1:
      if (draft.clientId !== null) return null
      if (state.client.kind !== "new") return "Найдите клиента или заведите нового"
      if (draft.clientName.trim().length === 0) return "Назовите имя — без него карточку не завести"
      if (!draft.consentPersonalData) {
        return "Без согласия на обработку данных карточку завести нельзя"
      }
      return null
    case 2:
      return draft.serviceIds.length === 0 ? "Выберите хотя бы одну услугу" : null
    case 3:
      // Мастер НЕ обязателен: «запись без мастера» — законный сценарий (колонка
      // неразобранных). Шаг существует, чтобы выбор был сделан осознанно, а не
      // чтобы его требовать.
      return null
    case 4:
      return null
    case 5:
      return null
    default:
      return null
  }
}

/** Подпись кнопки внизу: «Дальше» на 1–4, «Записать» на итоге (макет 5-А2). */
export function stepSubmitLabel(step: BookingStep, hasWarnings: boolean): string {
  if (step < BOOKING_STEP_COUNT) return "Дальше"
  // Пока предупреждение не разобрано, кнопка НЕ говорит «Записать»: она сказала бы
  // неправду — нажатие ничего не создаст, а вернёт тот же вопрос.
  return hasWarnings ? "Сначала решите предупреждение" : "Записать"
}

/**
 * Первый шаг, который ещё не пройден.
 *
 * Нужен не для запрета, а для возврата: после «это время только что заняли» человека
 * надо вернуть на шаг времени, а не на первый.
 */
export function firstIncompleteStep(state: BookingState): BookingStep {
  for (const step of [1, 2, 3, 4] as const) {
    if (stepBlocker(state, step) !== null) return step
  }
  return 5
}

/**
 * Новый состав услуг после нажатия на одну.
 *
 * Длительность складывается ЗДЕСЬ, а не в редьюсере: каталог знает только форма,
 * и сумма, посчитанная в одном месте с составом, не может с ним разойтись.
 *
 * Порядок сохраняется — сервер считает шаги подряд в том порядке, в каком их прислали,
 * и «стрижка, потом борода» не то же самое, что наоборот.
 */
export function toggleService(
  selected: readonly string[],
  services: readonly BookableService[],
  serviceId: string,
): { readonly serviceIds: readonly string[]; readonly durationMinutes: number } {
  const next = selected.includes(serviceId)
    ? selected.filter((id) => id !== serviceId)
    : [...selected, serviceId]
  return { serviceIds: next, durationMinutes: totalDuration(next, services) }
}

export function totalDuration(
  selected: readonly string[],
  services: readonly BookableService[],
): number {
  return selected.reduce((sum, id) => {
    const service = services.find((item) => item.id === id)
    return sum + (service?.durationMinutes ?? 0)
  }, 0)
}

export function totalPriceMinor(
  selected: readonly string[],
  services: readonly BookableService[],
): number | null {
  let sum = 0
  for (const id of selected) {
    const service = services.find((item) => item.id === id)
    // Хотя бы одна услуга без цены — и сумма визита неизвестна целиком. Показать
    // сумму остальных значит назвать клиенту цену, которая окажется не той.
    if (service?.priceMinor === null || service?.priceMinor === undefined) return null
    sum += service.priceMinor
  }
  return selected.length === 0 ? null : sum
}

/** «Мужская стрижка + Борода · 60 мин» — состав визита одной строкой. */
export function servicesSummary(
  selected: readonly string[],
  services: readonly BookableService[],
): string | null {
  const names = selected
    .map((id) => services.find((item) => item.id === id)?.name)
    .filter((name): name is string => name !== undefined)
  if (names.length === 0) return null
  return `${names.join(" + ")} · ${String(totalDuration(selected, services))} мин`
}
